import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { API_BASE } from '../../lib/config';
import {
  SparklesIcon,
  StarIcon,
  ChatBubbleLeftRightIcon,
  TicketIcon,
  CurrencyYenIcon,
  AdjustmentsHorizontalIcon,
  CheckCircleIcon,
  XMarkIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';

const OptionalFeatures = ({ onBack }) => {
  const [features, setFeatures] = useState({
    premium_badges: {
      enabled: true,
      job_seeker_price: 500,
      employer_price: 1000,
      description: 'プロフィールに表示される有料バッジ機能'
    },
    scout_system: {
      enabled: false,
      allow_employer_dm: true,
      daily_scout_limit: 5,
      scout_price_monthly: 5000,
      description: '事業者から求職者へのスカウト機能'
    },
    coupon_system: {
      enabled: false,
      discount_percentage: 10,
      max_usage: 100,
      description: 'キャンペーン用クーポン発行機能'
    },
    priority_listing: {
      enabled: false,
      priority_listing_price: 3000,
      description: '有料オプションによる求人の優先表示'
    },
    verification_service: {
      enabled: true,
      verification_fee: 0,
      manual_review: true,
      auto_verification: false,
      description: '本人確認サービス（手数料無料）'
    },
    premium_support: {
      enabled: false,
      monthly_fee: 2000,
      priority_response: true,
      description: '優先サポートサービス'
    }
  });

  const [activeCoupons, setActiveCoupons] = useState([]);

  const [showCouponModal, setShowCouponModal] = useState(false);
  const [newCoupon, setNewCoupon] = useState({
    code: '',
    discount: 10,
    max_usage: 100,
    expires_at: ''
  });

  const handleFeatureToggle = (featureKey) => {
    setFeatures(prev => ({
      ...prev,
      [featureKey]: {
        ...prev[featureKey],
        enabled: !prev[featureKey].enabled
      }
    }));
    toast.success(`${featureKey}を${features[featureKey].enabled ? '無効' : '有効'}にしました`);
  };

  // Save setting to backend
  const updateBackendSetting = async (key, value) => {
    try {
      const token = localStorage.getItem('admin_token') || localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/admin/settings/${key}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ value: value })
      });
      if (!res.ok) throw new Error('Failed to update setting');
      toast.success('設定を保存しました');
    } catch (e) {
      console.error(e);
      toast.error('設定の保存に失敗しました');
    }
  };

  const handleFeatureUpdate = (featureKey, field, value) => {
    setFeatures(prev => ({
      ...prev,
      [featureKey]: {
        ...prev[featureKey],
        [field]: value
      }
    }));
  };

  // Initial load of settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const token = localStorage.getItem('admin_token') || localStorage.getItem('token');
        const res = await fetch(`${API_BASE}/admin/settings`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          // Map backend settings to local state if they exist
          setFeatures(prev => {
            const newFeatures = { ...prev };
            if (data.scout_price_monthly) {
              newFeatures.scout_system.scout_price_monthly = parseInt(data.scout_price_monthly);
            }
            if (data.priority_listing_price) {
              newFeatures.priority_listing.priority_listing_price = parseInt(data.priority_listing_price);
            }
            return newFeatures;
          });
        }
      } catch (e) {
        console.error(e);
      }
    };
    fetchSettings();
    fetchKycSettings();
  }, []);

  const fetchKycSettings = async () => {
    try {
      const token = localStorage.getItem('admin_token') || localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/admin/settings/kyc`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!res.ok) throw new Error(`GET settings failed: ${res.status}`);
      const data = await res.json();
      const mode = (data.approval_mode || 'manual').toLowerCase();
      setFeatures(prev => ({
        ...prev,
        verification_service: {
          ...prev.verification_service,
          manual_review: mode === 'manual',
          auto_verification: mode === 'auto'
        }
      }));
    } catch (e) {
      console.error(e);
      toast.error('KYC設定の取得に失敗しました');
    }
  };

  const updateKycSettings = async (mode) => {
    try {
      const token = localStorage.getItem('admin_token') || localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/admin/settings/kyc`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ approval_mode: mode })
      });
      if (!res.ok) throw new Error(`PUT settings failed: ${res.status}`);
      toast.success(`KYCを${mode === 'auto' ? '自動承認' : '手動承認'}に更新しました`);
    } catch (e) {
      console.error(e);
      toast.error('KYC設定の更新に失敗しました');
    }
  };

  useEffect(() => {
    fetchKycSettings();
  }, []);

  // Coupon Fetching
  const fetchCoupons = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_BASE}/admin/coupons`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setActiveCoupons(data);
      }
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    if (features.coupon_system.enabled) {
      fetchCoupons();
    }
  }, [features.coupon_system.enabled]);

  const handleCreateCoupon = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_BASE}/admin/coupons`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newCoupon)
      });

      if (!res.ok) {
        const err = await res.json();
        toast.error(err.detail || 'クーポンの作成に失敗しました');
        return;
      }

      toast.success('クーポンを作成しました');
      setShowCouponModal(false);
      setNewCoupon({
        code: '',
        discount: 10,
        max_usage: 100,
        expires_at: ''
      });
      fetchCoupons();
    } catch (e) {
      toast.error('エラーが発生しました');
    }
  };

  const handleDeleteCoupon = async (couponId) => {
    if (!window.confirm('このクーポンを削除しますか？')) return;
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_BASE}/admin/coupons/${couponId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('クーポンを削除しました');
        fetchCoupons();
      } else {
        toast.error('削除に失敗しました');
      }
    } catch (e) {
      toast.error('エラーが発生しました');
    }
  };

  const renderFeatureCard = (key, feature, icon, title) => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center">
          <div className="p-2 bg-blue-100 rounded-lg mr-3">
            {icon}
          </div>
          <div>
            <h3 className="text-lg font-medium text-gray-900">{title}</h3>
            <p className="text-sm text-gray-500">{feature.description}</p>
          </div>
        </div>
        <button
          onClick={() => handleFeatureToggle(key)}
          className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${feature.enabled ? 'bg-blue-600' : 'bg-gray-200'
            }`}
        >
          <span
            className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition duration-200 ease-in-out ${feature.enabled ? 'translate-x-5' : 'translate-x-0'
              }`}
          />
        </button>
      </div>

      {feature.enabled && (
        <div className="space-y-3 pt-4 border-t border-gray-200">
          {key === 'premium_badges' && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">求職者バッジ価格</label>
                  <input
                    type="number"
                    value={feature.job_seeker_price}
                    onChange={(e) => handleFeatureUpdate(key, 'job_seeker_price', parseInt(e.target.value))}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">事業者バッジ価格</label>
                  <input
                    type="number"
                    value={feature.employer_price}
                    onChange={(e) => handleFeatureUpdate(key, 'employer_price', parseInt(e.target.value))}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </>
          )}

          {key === 'scout_system' && (
            <>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700">ダイレクトメッセージ許可</span>
                <button
                  onClick={() => handleFeatureUpdate(key, 'allow_employer_dm', !feature.allow_employer_dm)}
                  className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${feature.allow_employer_dm ? 'bg-blue-600' : 'bg-gray-200'
                    }`}
                >
                  <span className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transform transition duration-200 ease-in-out ${feature.allow_employer_dm ? 'translate-x-4' : 'translate-x-0'
                    }`} />
                </button>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">1日のスカウト限度数</label>
                <input
                  type="number"
                  value={feature.daily_scout_limit}
                  onChange={(e) => handleFeatureUpdate(key, 'daily_scout_limit', parseInt(e.target.value))}
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="mt-4 border-t pt-4">
                <label className="block text-sm font-medium text-gray-700">スカウト月額料金 (円)</label>
                <p className="text-xs text-gray-500 mb-2">事業者がスカウト機能を利用するために支払う月額費用です。</p>
                <input
                  type="number"
                  value={feature.scout_price_monthly || 5000}
                  onChange={(e) => handleFeatureUpdate(key, 'scout_price_monthly', parseInt(e.target.value))}
                  onBlur={(e) => updateBackendSetting('scout_price_monthly', parseInt(e.target.value))}
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </>
          )}

          {key === 'priority_listing' && (
            <>
              <div className="bg-gray-50 p-3 rounded text-sm text-gray-600">
                <p>この機能を有効にすると、事業者は有料オプションを購入して、求人検索結果の上位に表示させることができるようになります。</p>
              </div>
              <div className="mt-4 border-t pt-4">
                <label className="block text-sm font-medium text-gray-700">優先表示月額料金 (円)</label>
                <p className="text-xs text-gray-500 mb-2">事業者が優先表示機能を利用するために支払う月額費用です。</p>
                <input
                  type="number"
                  value={feature.priority_listing_price || 3000}
                  onChange={(e) => handleFeatureUpdate(key, 'priority_listing_price', parseInt(e.target.value))}
                  onBlur={(e) => updateBackendSetting('priority_listing_price', parseInt(e.target.value))}
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </>
          )}

          {key === 'verification_service' && (
            <>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <div className="flex items-center">
                  <CheckCircleIcon className="w-5 h-5 text-blue-600 mr-2" />
                  <span className="text-sm font-medium text-blue-800">手数料無料サービス</span>
                </div>
                <p className="text-sm text-blue-700 mt-1">
                  本人確認サービスは手数料が一切かかりません。認証方法を選択してください。
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">認証方法の選択</label>
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <input
                        type="radio"
                        id="manual_verification"
                        name="verification_method"
                        checked={feature.manual_review && !feature.auto_verification}
                        onChange={async () => {
                          handleFeatureUpdate(key, 'manual_review', true);
                          handleFeatureUpdate(key, 'auto_verification', false);
                          await updateKycSettings('manual');
                        }}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                      />
                      <label htmlFor="manual_verification" className="ml-3 block text-sm text-gray-700">
                        <div className="font-medium">手動認証</div>
                        <div className="text-gray-500">運営スタッフが書類を確認して手動で承認</div>
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="radio"
                        id="auto_verification"
                        name="verification_method"
                        checked={!feature.manual_review && feature.auto_verification}
                        onChange={async () => {
                          handleFeatureUpdate(key, 'manual_review', false);
                          handleFeatureUpdate(key, 'auto_verification', true);
                          await updateKycSettings('auto');
                        }}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                      />
                      <label htmlFor="auto_verification" className="ml-3 block text-sm text-gray-700">
                        <div className="font-medium">自動認証</div>
                        <div className="text-gray-500">システムが自動的に書類を検証して承認</div>
                      </label>
                    </div>
                  </div>
                </div>

                <div className="pt-3 border-t border-gray-200">
                  <div className="text-sm text-gray-600">
                    <div className="font-medium mb-1">現在の設定:</div>
                    <div>認証方法: {feature.manual_review ? '手動認証' : '自動認証'}</div>
                    <div>手数料: 無料</div>
                  </div>
                </div>
              </div>
            </>
          )}

          {key === 'premium_support' && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">月額料金</label>
                  <input
                    type="number"
                    value={feature.monthly_fee}
                    onChange={(e) => handleFeatureUpdate(key, 'monthly_fee', parseInt(e.target.value))}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">優先対応</span>
                  <button
                    onClick={() => handleFeatureUpdate(key, 'priority_response', !feature.priority_response)}
                    className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${feature.priority_response ? 'bg-blue-600' : 'bg-gray-200'
                      }`}
                  >
                    <span className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transform transition duration-200 ease-in-out ${feature.priority_response ? 'translate-x-4' : 'translate-x-0'
                      }`} />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* 戻るボタン */}
      {onBack && (
        <div className="flex items-center">
          <button
            onClick={onBack}
            className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5 mr-2" />
            メニューに戻る
          </button>
        </div>
      )}

      {/* ヘッダー */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">オプション機能管理</h2>
        <div className="text-sm text-gray-500">
          有効機能: {Object.values(features).filter(f => f.enabled).length} / {Object.keys(features).length}
        </div>
      </div>

      {/* 機能一覧 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {renderFeatureCard(
          'premium_badges',
          features.premium_badges,
          <StarIcon className="w-6 h-6 text-blue-600" />,
          'プレミアムバッジ'
        )}

        {renderFeatureCard(
          'scout_system',
          features.scout_system,
          <ChatBubbleLeftRightIcon className="w-6 h-6 text-blue-600" />,
          'スカウトシステム'
        )}

        {renderFeatureCard(
          'coupon_system',
          features.coupon_system,
          <TicketIcon className="w-6 h-6 text-blue-600" />,
          'クーポンシステム'
        )}

        {renderFeatureCard(
          'priority_listing',
          features.priority_listing,
          <AdjustmentsHorizontalIcon className="w-6 h-6 text-blue-600" />,
          '優先表示機能'
        )}

        {renderFeatureCard(
          'verification_service',
          features.verification_service,
          <CheckCircleIcon className="w-6 h-6 text-blue-600" />,
          '本人確認サービス'
        )}

        {renderFeatureCard(
          'premium_support',
          features.premium_support,
          <SparklesIcon className="w-6 h-6 text-blue-600" />,
          'プレミアムサポート'
        )}
      </div>

      {/* クーポン管理 */}
      {features.coupon_system.enabled && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">アクティブクーポン</h3>
            <button
              onClick={() => setShowCouponModal(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center"
            >
              <TicketIcon className="w-4 h-4 mr-2" />
              新規作成
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    クーポンコード
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    割引率
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    使用状況
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    期限
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {activeCoupons.map((coupon) => (
                  <tr key={coupon.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{coupon.code}</div>
                      <div className="text-sm text-gray-500">
                        作成: {new Date(coupon.created_at).toLocaleDateString('ja-JP')}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 bg-green-100 text-green-800 text-sm rounded-full">
                        {coupon.discount}% OFF
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {coupon.usage_count} / {coupon.max_usage}
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                        <div
                          className="bg-blue-600 h-2 rounded-full"
                          style={{ width: `${(coupon.usage_count / coupon.max_usage) * 100}%` }}
                        ></div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(coupon.expires_at).toLocaleDateString('ja-JP')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleDeleteCoupon(coupon.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        削除
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* クーポン作成モーダル */}
      {showCouponModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-1/2 shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-semibold text-gray-900">新規クーポン作成</h3>
              <button
                onClick={() => setShowCouponModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleCreateCoupon} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  クーポンコード
                </label>
                <input
                  type="text"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={newCoupon.code}
                  onChange={(e) => setNewCoupon({ ...newCoupon, code: e.target.value.toUpperCase() })}
                  placeholder="SUMMER2024"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    割引率 (%)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={newCoupon.discount}
                    onChange={(e) => setNewCoupon({ ...newCoupon, discount: parseInt(e.target.value) })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    最大使用回数
                  </label>
                  <input
                    type="number"
                    min="1"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={newCoupon.max_usage}
                    onChange={(e) => setNewCoupon({ ...newCoupon, max_usage: parseInt(e.target.value) })}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  有効期限
                </label>
                <input
                  type="date"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={newCoupon.expires_at}
                  onChange={(e) => setNewCoupon({ ...newCoupon, expires_at: e.target.value })}
                />
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowCouponModal(false)}
                  className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
                >
                  キャンセル
                </button>
                <button
                  type="submit"
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                >
                  <TicketIcon className="w-4 h-4 mr-2" />
                  作成
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default OptionalFeatures;